/**
 * Utility functions for Doters flexible passenger editing feature.
 *
 * This module provides helpers to determine when Doters users can edit
 * passenger name fields in flexible redemption mode.
 */

/**
 * List of field names that are editable in Doters flexible mode.
 * These are the passenger name fields that can be edited when flexible
 * redemption is enabled.
 */
export const FLEXIBLE_EDITABLE_FIELDS = ['firstName', 'lastName', 'secondLastName'];

/**
 * Check if Doters flexible redemption mode is enabled for the current wallet.
 *
 * In flexible redemption mode, Doters users can edit name fields that would
 * normally be disabled (locked to their account name).
 *
 * @param {Object} params - Function parameters
 * @param {Array} params.flexibleMatchingConfig - DOTERS_FLEXIBLE_PASSENGER_MATCHING feature flag value
 * @param {string} params.walletType - Current wallet type (doters, costapass, siempreplus, etc.)
 * @returns {boolean} True if flexible redemption is enabled
 *
 * @example
 * isDotersFlexibleRedeemEnabled({
 *   flexibleMatchingConfig: ['redeem'],
 *   walletType: 'doters'
 * }); // returns true
 *
 * @example
 * isDotersFlexibleRedeemEnabled({
 *   flexibleMatchingConfig: ['reward'],
 *   walletType: 'doters'
 * }); // returns false (only 'redeem' enables flexible mode)
 */
export const isDotersFlexibleRedeemEnabled = ({ flexibleMatchingConfig, walletType }) => {
  return (
    Array.isArray(flexibleMatchingConfig) &&
    flexibleMatchingConfig.includes('redeem') &&
    walletType === 'doters'
  );
};

/**
 * Determines if the first passenger is the same as the account holder (Doters user)
 * Compares firstName and lastName between the first passenger and the logged-in user
 * Ignores optional fields like secondFirstName/secondLastName
 *
 * @param {Object} params - Function parameters
 * @param {Object} params.purchase - Purchase object with passengers array
 * @param {Object} params.user - Logged in user from siemprePlus (has firstName, lastName, loyaltyName)
 * @returns {boolean|undefined} True if first passenger matches user or no passengers yet, false if different, undefined if not applicable
 */
export const isAccountHolder = ({ purchase, user }) => {
  // If no user, return undefined
  if (!user) {
    return undefined;
  }

  // Only applicable for Doters wallet (check user's loyaltyName)
  const userLoyaltyName = user.loyaltyName?.toLowerCase();
  if (userLoyaltyName !== 'doters') {
    return undefined;
  }

  // If no passengers yet (Purchase Attempt moment), assume first passenger will be the user
  if (!purchase?.passengers || purchase.passengers.length === 0) {
    return true;
  }

  const firstPassenger = purchase.passengers[0];

  // If passenger names are empty, we can't determine yet if it's the account holder
  // This prevents false negatives when the form is pre-populated with empty strings
  const passengerHasNames = firstPassenger.firstName || firstPassenger.lastName;
  if (!passengerHasNames) {
    return true; // Assume it will be the account holder until proven otherwise
  }

  // Normalize strings for comparison (lowercase and trim)
  /**
   * Normalize string for comparison
   * @param {string} str - String to normalize
   * @returns {string} Normalized string
   */
  const normalizeString = (str) => (str ? str.toLowerCase().trim() : '');

  // Compare ONLY firstName and lastName (ignore secondFirstName/secondLastName)
  const passengerFirstName = normalizeString(firstPassenger.firstName);
  const passengerLastName = normalizeString(firstPassenger.lastName);
  const userFirstName = normalizeString(user.firstName);
  const userLastName = normalizeString(user.lastName);

  const firstNameMatches = passengerFirstName === userFirstName;
  const lastNameMatches = passengerLastName === userLastName;
  const isMatch = firstNameMatches && lastNameMatches;

  return isMatch;
};

export default {
  FLEXIBLE_EDITABLE_FIELDS,
  isDotersFlexibleRedeemEnabled,
  isAccountHolder,
};
